import React from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { GRID_ACTION_TYPES, GridActionType, SetState } from "../../../assets/types";
import { ClaimHelper, isGridActionType } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { LoadingIndicator } from "../../../assets/vfx";
import { RssFeedPage } from "./rssFeedPage";
import { RssFeedForm } from "./rssFeedForm";
import { RssFeedView } from "./rssFeedView";
import { RssFeedService } from "./rssFeedService";
import { RssFeedDto } from "../../../services/dto";
import { getTitle } from "./rssFeedUtils";
import { useCommunity } from "../../../context/communityContext";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

interface RssFeedContainerProps {
  rssFeedTypeId?: string;
}

export const RssFeedContainer = (props: RssFeedContainerProps) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { communityId } = useCommunity();
  const { communityId: routeCommunityId } = useParams<{
    communityId?: string;
  }>();

  const claimCommunityId = !routeCommunityId
    ? ClaimHelper.getCommunityId()
    : undefined;

  const [data, setData] = React.useState<RssFeedDto>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );

  const [overrideCommunityId, setOverrideCommunityId] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
    setOverrideCommunityId(undefined);
  }, [communityId]);
  const resolvedCommunityId = React.useMemo(
      () => overrideCommunityId ?? routeCommunityId ?? communityId ?? claimCommunityId,
      [overrideCommunityId, routeCommunityId, communityId, claimCommunityId]
    );

  const setCommunityAdapter: SetState<string | undefined> = (value) => {
    if (typeof value === "function") {
      setOverrideCommunityId((prev) => ((value as (p: string | undefined) => string | undefined)(prev)));
    } else {
      setOverrideCommunityId((value));
    }
  };

  const [rssFeedTypeId, setRssFeedTypeId] = React.useState<string | undefined>(
    props.rssFeedTypeId
  );

  const rssfeedQuery = RssFeedService.getRssFeedQuery(
    data?.id ?? Constants.emptyGuid
  );
  const lookupQuery = RssFeedService.getLookups();
  const { mutateAsync: saveRssFeed } = RssFeedService.save();
  const { mutate: deleteMutate } = RssFeedService.deleteRssFeed();

  const title: string = getTitle(props.rssFeedTypeId);
  const isEditMode: boolean = !!(data?.id && data.id !== Constants.emptyGuid);
  const closeDrawer = (): void => {
      setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }

  const refreshGrid = (): void => {
    isEditMode && rssfeedQuery.refetch();
    queryClient.invalidateQueries({ queryKey: ["rssFeedGridQuery"] });
    closeDrawer();
  };

  const handleApiResponse = (response: any, successMessage: string): void => {
    const errorMsg = apiErrorMessage(response.errorMessages);
    if (errorMsg) {
      showSnackbar(errorMsg, MessageSeverity.Error);
    } else {
      showSnackbar(successMessage, MessageSeverity.Success);
      refreshGrid();
    }
  };

  const handleSubmit = async (values: RssFeedDto) => {
    values.id = isEditMode ? data!.id : Constants.emptyGuid;
    const message = isEditMode ? `${title} updated` : `${title} saved`;

    return await saveRssFeed(values, {
      onSuccess: (response) => handleApiResponse(response, message),
    });
  };

  const handleDelete = (): void => {
    deleteMutate(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) => handleApiResponse(response, `${title} deleted`),
    });
  };

  const handleCustomEvent = (actionType: string, dataId: string): void => {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as RssFeedDto);
      setSearchAction(actionType, dataId);
    }
  };

  const handleEdit = (): void => {
    setData(rssfeedQuery.data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
    setSearchAction(GRID_ACTION_TYPES.EDIT, rssfeedQuery.data?.id);
  };

  const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);

  const renderDrawerContent = (): React.JSX.Element | null => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          <RssFeedForm
            show={true}
            onHide={closeDrawer}
            onSave={handleSubmit}
            lookups={lookupQuery?.data}
            communityId={resolvedCommunityId}
            rssFeedTypeId={rssFeedTypeId}
            title={title}
            disableCommunity={!!routeCommunityId || !!claimCommunityId}
            disableRSSFeedType={!!props.rssFeedTypeId}
          />
        );

      case GRID_ACTION_TYPES.EDIT:
        return (
          <RssFeedForm
            data={rssfeedQuery.data}
            loading={rssfeedQuery.isLoading}
            show={true}
            onHide={closeDrawer}
            onSave={handleSubmit}
            lookups={lookupQuery?.data}
            communityId={resolvedCommunityId}
            rssFeedTypeId={rssFeedTypeId}
            title={title}
            disableCommunity={!!routeCommunityId || !!claimCommunityId}
            disableRSSFeedType={!!props.rssFeedTypeId}
          />
        );

      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return (
          <RssFeedView
            data={rssfeedQuery?.data}
            show={true}
            onHide={closeDrawer}
            onDelete={handleDelete}
            onEdit={handleEdit}
            lookups={lookupQuery?.data}
            title={title}
          />
        );

      default:
        return null;
    }
  };

  if (lookupQuery.isLoading) {
    return <LoadingIndicator isFullPage />;
  }

  return (
    <>
      <RssFeedPage
        onCustomEvent={handleCustomEvent}
        communityId={resolvedCommunityId}
        rssFeedTypeId={props.rssFeedTypeId}
        lookups={lookupQuery?.data}
        setCommunity={setCommunityAdapter}  
        setRssFeedType={setRssFeedTypeId}
        title={title}
        hideTitle={!!routeCommunityId}
      />
      {renderDrawerContent()}
    </>
  );
};
